/**
 * Custom Segment Card
 * Displays a dynamic segment with EXACTLY the same styling as SegmentCard
 */

import React, { useState, memo } from 'react';
import { Loader2 } from 'lucide-react';
import { DynamicSegment } from '../../services/segmentationService';
import { cn } from '../../lib/utils';
import { getZIndexClass } from '../../config/zIndex';

interface CustomSegmentCardProps {
  segment: DynamicSegment;
  segmentIndex: number;
  segmentationId: number;
  segmentationAxis: string;
  totalPersonasInSegmentation: number;
  isSelected: boolean;
  onToggle: (segmentationId: number, segmentIndex: number, personaIds: number[]) => void;
  onViewPersonas?: (segmentationId: number, segmentIndex: number) => void;
  className?: string;
  isLoading?: boolean;
  loadingPersonas?: boolean;
}

export const CustomSegmentCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={cn(
      'relative border rounded-lg p-4 border-gray-200 bg-white animate-pulse',
      className,
    )}
  >
    <div className="absolute top-3 right-3 z-10">
      <div className="w-5 h-5 rounded border-2 border-gray-200 bg-gray-100" />
    </div>
    <div className="w-full">
      <div className="flex justify-between items-start mb-2 pr-8">
        <div className="h-6 bg-gray-200 rounded w-1/3" />
      </div>
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 bg-gray-200 rounded w-24" />
        <div className="h-4 bg-gray-200 rounded w-28" />
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-100 rounded w-full" />
        <div className="h-3 bg-gray-100 rounded w-4/5" />
      </div>
    </div>
  </div>
);

const CustomSegmentCard: React.FC<CustomSegmentCardProps> = ({
  segment,
  segmentIndex,
  segmentationId,
  totalPersonasInSegmentation,
  isSelected,
  onToggle,
  onViewPersonas,
  className = '',
  isLoading = false,
  loadingPersonas = false,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleCardClick = () => {
    onToggle(segmentationId, segmentIndex, segment.persona_ids);
  };

  const percentOfTotal =
    totalPersonasInSegmentation > 0
      ? ((segment.persona_count / totalPersonasInSegmentation) * 100).toFixed(1)
      : '0.0';

  return (
    <div className="relative">
      {/* Checkbox indicator in top-right corner */}
      <div className="absolute top-3 right-3 z-10">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(segmentationId, segmentIndex, segment.persona_ids)}
            className="sr-only"
          />
          <div
            className={cn(
              'w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200',
              isSelected
                ? 'bg-[#07E5D1] border-[#07E5D1]'
                : 'bg-white border-gray-300 hover:border-gray-400',
            )}
          >
            {isSelected && (
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
        </label>
      </div>

      <div
        className={cn(
          'border rounded-lg p-4 transition-all duration-200 cursor-pointer',
          isSelected
            ? 'bg-blue-50 border-blue-300 shadow-md'
            : 'border-gray-200 hover:border-blue-400 hover:shadow-sm',
          className,
        )}
        onClick={handleCardClick}
      >
        <div className="w-full">
          {/* Header with Title */}
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-medium text-gray-900">{segment.label}</h3>
          </div>

          {/* Profile Count and View All Button */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              {isLoading ? (
                <span className="font-medium text-blue-600 flex items-center">
                  <div className="flex space-x-1 items-center">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                    <div
                      className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"
                      style={{ animationDelay: '0.4s' }}
                    ></div>
                  </div>
                </span>
              ) : loadingPersonas ? (
                <span className="text-sm text-blue-600 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading profiles...
                </span>
              ) : onViewPersonas ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewPersonas(segmentationId, segmentIndex);
                  }}
                  title="Click to see all profiles"
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                >
                  {segment.persona_count} profiles ({percentOfTotal}%)
                </button>
              ) : (
                <span>
                  {segment.persona_count} profiles ({percentOfTotal}%)
                </span>
              )}
            </div>

            {/* Segment Details button hidden per request
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDetailsModal(true);
              }}
              title="Click to see segment details"
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
            >
              <Info className="w-4 h-4 mr-1" />
              Segment Details
            </button>
            */}
          </div>

          {/* Segment Description */}
          {segment.description && (
            <div className="text-sm text-gray-600 leading-relaxed">{segment.description}</div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${getZIndexClass('dialog')} p-4`}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{segment.label} - Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* Description */}
              {segment.description && (
                <div className="text-sm text-gray-600 leading-relaxed mb-6">
                  {segment.description}
                </div>
              )}

              {/* Defining Characteristics */}
              {segment.defining_characteristics && segment.defining_characteristics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    Defining Characteristics
                  </h3>
                  <ul className="space-y-2">
                    {segment.defining_characteristics.map((char, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                        {char}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(CustomSegmentCard);
